import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import API from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import './ServiceDetailPage.css';

/* ── Skeleton card (reuse same shape as ProductCard) ──────────── */
const SkeletonCard = () => (
  <div className="product-card skeleton-card" style={{ pointerEvents: 'none' }}>
    <div className="skeleton-img" style={{ height: 200, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'svc-shimmer 1.4s infinite', borderRadius: 'var(--radius)' }} />
    <div className="product-card-body" style={{ padding: '16px 12px' }}>
      <div className="skeleton-line short" style={{ height: 12, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'svc-shimmer 1.4s infinite', marginBottom: 8, width: '45%' }} />
      <div className="skeleton-line" style={{ height: 14, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'svc-shimmer 1.4s infinite', marginBottom: 8 }} />
      <div className="skeleton-line medium" style={{ height: 12, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'svc-shimmer 1.4s infinite', width: '70%' }} />
    </div>
  </div>
);

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate  = useNavigate();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await API.get(`/public-services/${slug}`);
        if (!cancelled) setService(data.service);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 404) setError('404');
          else setError('Failed to load service. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  /* 404 */
  if (!loading && error === '404') {
    return (
      <div className="svcd-page">
        <div className="container svcd-notfound">
          <h2>Service Not Found</h2>
          <p>This service doesn't exist or has been removed.</p>
          <Link to="/services" className="btn btn-primary">
            <FiArrowLeft size={16} /> Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {service && (
        <Helmet>
          <title>{service.name} — Angel Printers</title>
          <meta name="description" content={service.short_description || `Explore ${service.name} products from Angel Printers.`} />
        </Helmet>
      )}

      <div className="svcd-page">

        {/* ── HERO ────────────────────────────────────────────── */}
        <section className="svcd-hero">
          <div className="container">
            {/* Breadcrumb */}
            <nav className="svcd-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/services">Services</Link>
              {service && <><span>/</span><span>{service.name}</span></>}
            </nav>

            {loading ? (
              <div className="svcd-hero-skeleton">
                <div className="svcd-sk-tag" />
                <div className="svcd-sk-h1" />
                <div className="svcd-sk-p" />
              </div>
            ) : (
              <div className="svcd-hero-content">
                <span className="svcd-hero-label">OUR SERVICES</span>
                <h1 className="svcd-hero-title">{service?.name}</h1>
                {service?.short_description && (
                  <p className="svcd-hero-desc">{service.short_description}</p>
                )}
                <button
                  className="btn btn-outline"
                  onClick={() => navigate('/services')}
                  style={{ marginTop: 8 }}
                >
                  <FiArrowLeft size={15} /> All Services
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ── PRODUCTS GRID ────────────────────────────────────── */}
        <section className="svcd-products-section">
          <div className="container">

            {/* Loading */}
            {loading && (
              <div className="svcd-products-grid">
                {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            {/* Error */}
            {!loading && error && error !== '404' && (
              <div className="svcd-state-center">
                <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{error}</p>
                <button className="btn btn-outline" onClick={() => window.location.reload()}>
                  Try Again
                </button>
              </div>
            )}

            {/* No products */}
            {!loading && !error && service && service.products?.length === 0 && (
              <div className="svcd-state-center">
                <h3 style={{ marginBottom: 8 }}>No products yet</h3>
                <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                  We're adding products to this service soon. In the meantime, browse all products.
                </p>
                <Link to="/products" className="btn btn-primary">
                  Browse All Products <FiArrowRight size={15} />
                </Link>
              </div>
            )}

            {/* Products */}
            {!loading && !error && service?.products?.length > 0 && (
              <>
                <p className="svcd-count">
                  {service.products.length} product{service.products.length !== 1 ? 's' : ''} in this service
                </p>
                <div className="svcd-products-grid">
                  {service.products.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </>
            )}

          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────── */}
        {!loading && !error && (
          <section className="svcd-cta">
            <div className="container svcd-cta-inner">
              <div>
                <h3>Need a custom quote?</h3>
                <p>Tell us your requirements and get a personalised price within hours.</p>
              </div>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/get-quote')}>
                Get a Quote <FiArrowRight size={16} />
              </button>
            </div>
          </section>
        )}

      </div>
    </>
  );
};

export default ServiceDetailPage;
